import { useCallback, useEffect, useState } from 'react';
import { AModal, ATable, DropdownOptions, MenuOptions } from '../../../../components';
import { useToast } from '../../../../hooks/toast';
import {
  ICrmCampaign,
  ICrmRecipient,
  CrmRecipientStatus,
  getCrmCampaignRecipients,
  retryCrmRecipient,
  retryAllFailedCrmRecipients,
} from '../../../../http/crm';
import {
  EyeOutlined,
  ReloadOutlined,
  RetweetOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Space, Tag, Typography, Card, Row, Col, Statistic } from 'antd';

const { Text } = Typography;

interface IRecipientsModalProps {
  open: boolean;
  close: () => void;
  campaign: ICrmCampaign;
}

const STATUS_OPTIONS: { value: CrmRecipientStatus; label: string; color: string }[] = [
  { value: 'PENDING', label: 'Pendente', color: 'default' },
  { value: 'QUEUED', label: 'Na fila', color: 'processing' },
  { value: 'SENT', label: 'Enviado', color: 'blue' },
  { value: 'DELIVERED', label: 'Entregue', color: 'cyan' },
  { value: 'READ', label: 'Lido', color: 'success' },
  { value: 'FAILED', label: 'Falhou', color: 'error' },
];

const getStatusLabel = (st: CrmRecipientStatus) =>
  STATUS_OPTIONS.find((s) => s.value === st)?.label ?? st;
const getStatusColor = (st: CrmRecipientStatus) =>
  STATUS_OPTIONS.find((s) => s.value === st)?.color ?? 'default';

const RecipientsModal: React.FC<IRecipientsModalProps> = ({
  open,
  close,
  campaign,
}) => {
  const { addToast } = useToast();

  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<CrmRecipientStatus | undefined>(undefined);
  const [recipients, setRecipients] = useState<ICrmRecipient[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(50);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();
  const [retryingAll, setRetryingAll] = useState<boolean>(false);

  const listRecipients = useCallback(async () => {
    if (!campaign?.id) return;
    setLoading(true);
    setRecipients([]);
    try {
      const response = await getCrmCampaignRecipients(
        campaign.id,
        limit,
        (page - 1) * limit,
        search,
        status,
      );
      setPages(response.count);
      setRecipients(response.data);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao listar destinatários.',
      });
    } finally {
      setLoading(false);
    }
  }, [campaign?.id, limit, page, search, status, addToast]);

  const handleRetryOne = useCallback(async (record: ICrmRecipient) => {
    try {
      await retryCrmRecipient(campaign.id, record.id);
      addToast({
        type: 'success',
        title: 'Reenviado',
        description: `Tentativa de reenvio para ${record.phone} agendada.`,
      });
      listRecipients();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao retentar.',
      });
    }
  }, [addToast, campaign.id, listRecipients]);

  const handleRetryAllFailed = useCallback(async () => {
    if (!campaign?.id) return;
    setRetryingAll(true);
    try {
      const res = await retryAllFailedCrmRecipients(campaign.id, true);
      addToast({
        type: 'info',
        title: 'Retentativa em massa',
        description: `${res.retried} de ${res.total} destinatários reenfileirados.`,
      });
      listRecipients();
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: error?.response?.data?.error ?? 'Erro ao retentar em massa.',
      });
    } finally {
      setRetryingAll(false);
    }
  }, [campaign?.id, addToast, listRecipients]);

  useEffect(() => {
    if (open && campaign?.id) {
      listRecipients();
    }
  }, [open, campaign?.id, listRecipients]);

  const summary = {
    pending: recipients.filter((r) => r.status === 'PENDING').length,
    queued: recipients.filter((r) => r.status === 'QUEUED').length,
    sent: recipients.filter((r) => r.status === 'SENT').length,
    delivered: recipients.filter((r) => r.status === 'DELIVERED').length,
    read: recipients.filter((r) => r.status === 'READ').length,
    failed: recipients.filter((r) => r.status === 'FAILED').length,
  };

  return (
    <AModal
      open={open}
      close={close}
      title={
        <Space size={8}>
          <TeamOutlined /> Destinatários - {campaign?.name ?? 'Campanha'}
        </Space>
      }
      width={1000}
      footer={null}
    >
      <div style={{ marginBottom: 12 }}>
        <Row gutter={[8, 8]}>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" style={{ padding: 4 }}>
              <Statistic title="Pendentes" value={summary.pending} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" style={{ padding: 4 }}>
              <Statistic title="Na fila" value={summary.queued} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" style={{ padding: 4 }}>
              <Statistic title="Enviados" value={summary.sent} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" style={{ padding: 4 }}>
              <Statistic title="Entregues" value={summary.delivered} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" style={{ padding: 4 }}>
              <Statistic title="Lidos" value={summary.read} />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Card size="small" style={{ padding: 4, background: '#fff1f0' }}>
              <Statistic title="Falhas" value={summary.failed} valueStyle={{ color: '#cf1322' }} />
            </Card>
          </Col>
        </Row>
      </div>

      <MenuOptions
        showInputSearch
        search={search}
        setSearch={setSearch}
        menu_items={[
          {
            label: 'Atualizar',
            key: '1',
            icon: <ReloadOutlined />,
            onClick: () => listRecipients(),
          },
          {
            label: 'Retentar todos com falha',
            key: '2',
            icon: <RetweetOutlined />,
            onClick: handleRetryAllFailed,
            disabled: retryingAll || summary.failed === 0,
          },
        ]}
      />

      <div style={{ marginTop: 12 }}>
        <Space wrap size={[6, 6]} style={{ marginBottom: 12 }}>
          <Tag.CheckableTag
            checked={!status}
            onChange={() => setStatus(undefined)}
          >
            Todos
          </Tag.CheckableTag>
          {STATUS_OPTIONS.map((s) => (
            <Tag.CheckableTag
              key={s.value}
              checked={status === s.value}
              onChange={() => setStatus(status === s.value ? undefined : s.value)}
            >
              {s.label}
            </Tag.CheckableTag>
          ))}
        </Space>

        <ATable
          scroll={{ y: 400 }}
          columns={[
            {
              title: 'Cliente',
              dataIndex: 'client_name',
              width: 200,
              render: (value: string | null | undefined, record: ICrmRecipient) => (
                <Space direction="vertical" size={2}>
                  <Text strong>{value ?? '— Cliente não vinculado —'}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {record.phone}
                  </Text>
                </Space>
              ),
            },
            {
              title: 'Telefone',
              dataIndex: 'phone',
              width: 150,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 130,
              render: (value: CrmRecipientStatus) => (
                <Tag color={getStatusColor(value)}>
                  {getStatusLabel(value)}
                </Tag>
              ),
            },
            {
              title: 'Tentativas',
              dataIndex: 'retry_count',
              width: 90,
              align: 'center',
            },
            {
              title: 'Erro',
              dataIndex: 'error_message',
              render: (value: string | null | undefined) =>
                value ? (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {value}
                  </Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    —
                  </Text>
                ),
            },
            {
              title: 'Enviado em',
              dataIndex: 'message_sent_at',
              width: 150,
              render: (value: string) =>
                value ? new Date(value).toLocaleString('pt-BR') : '-',
            },
            {
              title: 'Opções',
              width: 110,
              render: (_: unknown, record: ICrmRecipient) => (
                <DropdownOptions
                  items={[
                    {
                      label: 'Retentar',
                      key: '1',
                      icon: <RetweetOutlined />,
                      onClick: () => handleRetryOne(record),
                    },
                    {
                      label: 'Detalhes',
                      key: '2',
                      icon: <EyeOutlined />,
                      onClick: () => {
                        const details = {
                          phone: record.phone,
                          status: record.status,
                          error: record.error_message,
                          payload: record.rendered_payload,
                        };
                        addToast({
                          type: 'info',
                          title: 'Detalhes do destinatário',
                          description: JSON.stringify(details, null, 2).slice(0, 200),
                        });
                      },
                    },
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={recipients}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </div>
    </AModal>
  );
};

export default RecipientsModal;
